import {
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCardDetail } from '../../context/CardDetailContext';
import {
  CreditCardRequest,
  CreditCardResponse2,
} from '../../model/Credit-card.model';
import type {
  CreditCardServerFilters,
  ScheduleMonthBucket,
} from '../../model/credit-card-filters';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import BankService from '../../service/bank-service/Bank.service';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import PartnerService from '../../service/partner-service/Partner.service';

import {
  getSearchCheckboxFilterProps,
  getSimpleCheckboxFilterProps,
} from '../../utils/table-filter-helper';

const { Text } = Typography;

interface CreditCardTableProps {
  creditCards: CreditCardResponse2[];
  onUpdateSuccess: () => void;
  onDeleteSuccess: () => void;
  selectedMonth?: number;
  selectedYear?: number;
  /** Khi có serverPagination: bộ lọc + refetch từ API (không dùng onFilter client) */
  filters?: CreditCardServerFilters;
  onFiltersChange?: (patch: Partial<CreditCardServerFilters>) => void;
  /** Server-side pagination (Ant Design controlled mode) */
  serverPagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
  };
}

interface EditCreditCardModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  creditCard: CreditCardResponse2 | null;
}

const EditCreditCardModal: React.FC<EditCreditCardModalProps> = ({
  visible,
  onClose,
  onSuccess,
  creditCard,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isSameAsCustomer, setIsSameAsCustomer] = useState(false);
  const [banks, setBanks] = useState<any[]>([]);
  const [cardTypes, setCardTypes] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  const customerName = creditCard?.customer?.name || '';

  // Load banks and card types when modal opens
  React.useEffect(() => {
    if (visible && creditCard) {
      const loadData = async () => {
        try {
          setDataLoading(true);
          const [banksData, cardTypesData] = await Promise.all([
            BankService.getBankList(),
            CardTypeService.getCardTypeList(),
          ]);
          setBanks(banksData);
          setCardTypes(cardTypesData);

          // Debug logs
          console.log('CreditCard data:', creditCard);
          console.log('CardTypes data:', cardTypesData);
          console.log('Banks data:', banksData);
        } catch (error) {
          console.error('Error loading banks and card types:', error);
        } finally {
          setDataLoading(false);
        }
      };
      loadData();
    }
  }, [visible, creditCard]);

  // Initialize form when modal opens
  React.useEffect(() => {
    if (visible && creditCard) {
      const isSame = creditCard.cardHolderName === creditCard.customer?.name;
      setIsSameAsCustomer(isSame);

      form.setFieldsValue({
        cardNumber: creditCard.cardNumber,
        cardTypeId: creditCard.cardType.id,
        bankId: creditCard.bank.id,
        expiryDate: creditCard.expiryDate,
        notes: creditCard.notes || '',
        creditLimit: creditCard.creditLimit || '',
        status: creditCard.status || undefined,
        defaultFeeRate: creditCard.defaultFeeRate || '',
        cyclePaymentDate: creditCard.cyclePaymentDate || '',
        cardHolderName: creditCard.cardHolderName || '',
      });
    }
  }, [visible, creditCard, form]);

  // Set form values when banks and cardTypes are loaded
  React.useEffect(() => {
    if (visible && creditCard && banks.length > 0 && cardTypes.length > 0) {
      form.setFieldsValue({
        cardNumber: creditCard.cardNumber,
        cardTypeId: creditCard.cardType.id,
        bankId: creditCard.bank.id,
        expiryDate: creditCard.expiryDate,
        notes: creditCard.notes || '',
        creditLimit: creditCard.creditLimit || '',
        status: creditCard.status || undefined,
        defaultFeeRate: creditCard.defaultFeeRate || '',
        cyclePaymentDate: creditCard.cyclePaymentDate || '',
        cardHolderName: creditCard.cardHolderName || '',
      });
    }
  }, [visible, creditCard, banks, cardTypes, form]);

  // Update card holder name when checkbox changes
  React.useEffect(() => {
    if (isSameAsCustomer) {
      form.setFieldsValue({
        cardHolderName: customerName,
      });
    }
  }, [isSameAsCustomer, customerName, form]);

  const handleCheckboxChange = (e: any) => {
    const checked = e.target.checked;
    setIsSameAsCustomer(checked);

    if (checked) {
      form.setFieldsValue({
        cardHolderName: customerName,
      });
    }
  };

  const handleSubmit = async () => {
    if (!creditCard) return;

    try {
      setLoading(true);
      const values = await form.validateFields();

      const updatedData: CreditCardRequest = {
        cardNumber: values.cardNumber,
        cardTypeId: values.cardTypeId,
        bankId: values.bankId,
        expiryDate: values.expiryDate,
        notes: values.notes,
        creditLimit: values.creditLimit,
        status: values.status,
        defaultFeeRate: values.defaultFeeRate,
        cyclePaymentDate: values.cyclePaymentDate,
        cardHolderName: values.cardHolderName,
        customerId: creditCard.customer?.id,
      };

      await CreditCardService.updateCreditCard(creditCard.id, updatedData);
      message.success('Cập nhật thẻ tín dụng thành công');
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error: any) {
      console.error('Error updating credit card:', error);
      if (error.message?.includes('Số thẻ đã tồn tại')) {
        message.error('Số thẻ đã tồn tại trong hệ thống!');
      } else {
        message.error('Có lỗi xảy ra khi cập nhật thẻ tín dụng');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: string) => {
    const number = value.replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  return (
    <Modal
      title="Chỉnh sửa thông tin thẻ"
      open={visible}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      onOk={handleSubmit}
      okText="Hoàn thành"
      cancelText="Hủy"
      width={600}
      confirmLoading={loading}
      destroyOnClose
      maskClosable={false}
    >
      {creditCard && (
        <Form form={form} layout="vertical">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<Text strong>Số thẻ</Text>}
              name="cardNumber"
              rules={[{ required: true, message: 'Vui lòng nhập số thẻ!' }]}
            >
              <Input placeholder="Nhập số thẻ" />
            </Form.Item>

            <Form.Item
              label={<Text strong>Tên chủ thẻ</Text>}
              name="cardHolderName"
            >
              <div className="space-y-2">
                <Input
                  placeholder={
                    isSameAsCustomer
                      ? 'Sử dụng tên khách hàng'
                      : 'Nhập tên chủ thẻ'
                  }
                  disabled={isSameAsCustomer}
                />
                <Checkbox
                  checked={isSameAsCustomer}
                  onChange={handleCheckboxChange}
                >
                  Sử dụng tên khách hàng ({customerName})
                </Checkbox>
              </div>
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngân hàng</Text>}
              name="bankId"
              rules={[{ required: true, message: 'Vui lòng chọn ngân hàng!' }]}
            >
              <Select
                placeholder={`Chọn ngân hàng (hiện tại: ${creditCard.bank?.name || 'N/A'})`}
                loading={dataLoading}
                showSearch
                notFoundContent={
                  dataLoading ? 'Đang tải...' : 'Không tìm thấy ngân hàng'
                }
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={dataLoading}
              >
                {dataLoading ? (
                  <Select.Option value="">Đang tải...</Select.Option>
                ) : (
                  banks.map((bank) => (
                    <Select.Option key={bank.id} value={bank.id}>
                      {bank.name}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Loại thẻ</Text>}
              name="cardTypeId"
              rules={[{ required: true, message: 'Vui lòng chọn loại thẻ!' }]}
            >
              <Select
                placeholder={`Chọn loại thẻ (hiện tại: ${creditCard.cardType?.name || 'N/A'})`}
                loading={dataLoading}
                showSearch
                notFoundContent={
                  dataLoading ? 'Đang tải...' : 'Không tìm thấy loại thẻ'
                }
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                disabled={dataLoading}
              >
                {dataLoading ? (
                  <Select.Option value="">Đang tải...</Select.Option>
                ) : (
                  cardTypes.map((cardType) => (
                    <Select.Option
                      key={cardType.cardTypeId}
                      value={cardType.cardTypeId}
                    >
                      {cardType.name}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngày hết hạn</Text>}
              name="expiryDate"
            >
              <Input type="date" />
            </Form.Item>

            <Form.Item
              label={<Text strong>Hạn mức tín dụng</Text>}
              name="creditLimit"
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  value ? formatCurrency(value.toString()) : ''
                }
                parser={(value) => (value ? value.replace(/\./g, '') : '')}
                placeholder="Nhập hạn mức"
              />
            </Form.Item>

            <Form.Item
              label={<Text strong>Trạng thái hoạt động</Text>}
              name="status"
            >
              <Select placeholder="Chọn trạng thái">
                <Select.Option value="LUU_THE">Lưu thẻ</Select.Option>
                <Select.Option value="TRA_THE">Trả thẻ</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<Text strong>Phí mặc định (%)</Text>}
              name="defaultFeeRate"
              help="Phí mặc định sẽ được áp dụng khi tạo lịch đáo hạn"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={2}
                addonAfter="%"
                placeholder="Nhập phí mặc định (0-100%)"
              />
            </Form.Item>

            <Form.Item
              label={<Text strong>Ngày đến hạn hàng tháng</Text>}
              name="cyclePaymentDate"
              help="Ngày thanh toán của thẻ trong mỗi tháng (1-30)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={30}
                precision={0}
                placeholder="Nhập ngày đến hạn (1-30)"
              />
            </Form.Item>

            <Form.Item label={<Text strong>Khách hàng</Text>}>
              <Input value={creditCard.customer?.name} disabled />
            </Form.Item>
          </div>

          <Form.Item label={<Text strong>Ghi chú</Text>} name="notes">
            <Input.TextArea placeholder="Thêm ghi chú" rows={3} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

const ViewCreditCardModal: React.FC<any> = ({
  visible,
  onClose,
  creditCard,
}) => (
  <Modal
    title="Chi tiết thẻ tín dụng"
    open={visible}
    onCancel={onClose}
    footer={null}
  >
    <p>Modal xem chi tiết thẻ tín dụng sẽ được implement sau</p>
  </Modal>
);

const CreditCardTable: React.FC<CreditCardTableProps> = ({
  creditCards,
  onUpdateSuccess,
  onDeleteSuccess,
  selectedMonth,
  selectedYear,
  filters,
  onFiltersChange,
  serverPagination,
}) => {
  const useServerFilters = Boolean(
    serverPagination && filters && onFiltersChange
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CreditCardResponse2 | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const { setSelectedCard: setCardDetailCard } = useCardDetail();
  const navigate = useNavigate();
  const [bankOptions, setBankOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [partnerOptions, setPartnerOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return '0 ₫';
    return `${amount.toLocaleString('vi-VN')} ₫`;
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber || cardNumber.length < 4) return 'N/A';
    return `***${cardNumber.slice(-4)}`;
  };

  const getStatusTag = (status: string | undefined) => {
    switch (status) {
      case 'TRA_THE':
        return <Tag color="blue">Trả thẻ</Tag>;
      case 'LUU_THE':
        return <Tag color="green">Lưu thẻ</Tag>;
      default:
        return null; // Don't show tag for undefined or unknown status
    }
  };

  const getNewStatusTag = (newStatus: string | undefined) => {
    switch (newStatus) {
      case 'DANG_LAM':
        return <Tag color="processing">Đang làm</Tag>;
      case 'DANG_CHO':
        return <Tag color="warning">Đang chờ</Tag>;
      case 'KHONG_LAM':
        return <Tag color="error">Không làm</Tag>;
      default:
        return <Tag color="default">Đang chờ</Tag>;
    }
  };

  const getActiveStatusTag = (isActive: boolean) => {
    return isActive ? (
      <Tag color="success">Còn hạn</Tag>
    ) : (
      <Tag color="error">Hết hạn</Tag>
    );
  };

  const handleEdit = (record: CreditCardResponse2) => {
    setSelectedCard(record);
    setEditModalVisible(true);
  };

  const handleView = (record: CreditCardResponse2) => {
    setSelectedCard(record);
    setViewModalVisible(true);
  };

  const handleSoftDelete = (record: CreditCardResponse2) => {
    Modal.confirm({
      title: 'Xác nhận vô hiệu hóa thẻ',
      content: (
        <div>
          <p>
            Bạn có chắc chắn muốn <strong>vô hiệu hóa</strong> thẻ tín dụng "
            {formatCardNumber(record.cardNumber)}"?
          </p>
          <p className="text-orange-600 font-medium">
            ⚠️ Thẻ sẽ được chuyển sang trạng thái "Không làm" và ngừng hoạt
            động.
          </p>
          <p className="text-gray-600 text-sm">
            Lưu ý: Dữ liệu sẽ được giữ lại và có thể khôi phục sau này.
          </p>
        </div>
      ),
      okText: 'Vô hiệu hóa',
      cancelText: 'Hủy',
      okType: 'danger',
      icon: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
      onOk: async () => {
        setLoading(true);
        try {
          await CreditCardService.softDeleteCreditCard(record.id);
          message.success('Vô hiệu hóa thẻ tín dụng thành công');
          onDeleteSuccess();
        } catch (error: any) {
          message.error(
            error.message || 'Có lỗi xảy ra khi vô hiệu hóa thẻ tín dụng'
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleHardDelete = (record: CreditCardResponse2) => {
    Modal.confirm({
      title: 'Xác nhận xóa vĩnh viễn',
      content: (
        <div>
          <p>
            Bạn có chắc chắn muốn <strong>xóa vĩnh viễn</strong> thẻ tín dụng "
            {formatCardNumber(record.cardNumber)}"?
          </p>
          <p className="text-red-600 font-medium">
            ⚠️ CẢNH BÁO: Hành động này sẽ xóa thẻ và tất cả dữ liệu liên quan:
          </p>
          <ul className="text-red-600 text-sm ml-4 list-disc">
            <li>Tất cả lịch đáo hạn (MaturitySchedule)</li>
            <li>Tất cả giao dịch (Transaction)</li>
            <li>Tất cả khoản thanh toán (Payment)</li>
          </ul>
          <p className="text-red-600 font-medium text-sm">
            ❌ Hành động này KHÔNG THỂ HOÀN TÁC!
          </p>
          <p className="text-gray-600 text-sm">
            Lưu ý: Việc xóa này không ảnh hưởng đến số dư tiền.
          </p>
        </div>
      ),
      okText: 'XÓA VĨNH VIỄN',
      cancelText: 'Hủy',
      okType: 'danger',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      onOk: async () => {
        setLoading(true);
        try {
          await CreditCardService.hardDeleteCreditCard(record.id);
          message.success('Xóa thẻ tín dụng vĩnh viễn thành công');
          onDeleteSuccess();
        } catch (error: any) {
          message.error(error.message || 'Có lỗi xảy ra khi xóa thẻ tín dụng');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleToggleNewStatus = async (record: CreditCardResponse2) => {
    try {
      setLoading(true);
      await CreditCardService.toggleCreditCardNewStatus(record.id);
      onUpdateSuccess();
    } catch (error) {
      console.error('Error toggling new status:', error);
      message.error('Không thể cập nhật trạng thái');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCardDetail = (record: CreditCardResponse2) => {
    setCardDetailCard(record);
    navigate('/card-detail');
  };

  const getColumnSearchProps = (
    dataIndex: string,
    title: string,
    customFilterFn?: (value: any, record: any) => boolean
  ) => {
    const SearchFilter = ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => {
      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Tìm ${title.toLowerCase()}`}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              // Auto filter as user types
              setTimeout(() => confirm({ closeDropdown: false }), 100);
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa bộ lọc
            </Button>
          </Space>
        </div>
      );
    };

    return {
      filterDropdown: SearchFilter,
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: customFilterFn
        ? customFilterFn
        : (value: any, record: any) => {
          const raw = Array.isArray(value) ? value[0] : value;
          if (raw == null || (typeof raw === 'string' && !raw.trim())) return true;
          const searchStr = (raw != null ? raw.toString() : '').trim().toLowerCase();
          const recordValue = dataIndex.includes('.')
            ? dataIndex.split('.').reduce((obj, key) => obj?.[key], record)
            : record[dataIndex];
          if (recordValue == null) return false;
          const recordStr = recordValue.toString().toLowerCase();
          return recordStr.includes(searchStr);
        },
    };
  };

  const getSelectFilterProps = (dataIndex: string, options: any[]) => {
    const SelectFilter = ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => {
      return (
        <div style={{ padding: 8 }}>
          <Select
            mode="multiple"
            style={{ width: '100%', marginBottom: 8 }}
            placeholder="Chọn để lọc (nhiều lựa chọn)"
            value={selectedKeys}
            onChange={(value) => {
              setSelectedKeys(value);
              // Auto filter when selection changes
              setTimeout(() => confirm({ closeDropdown: false }), 100);
            }}
            allowClear
            maxTagCount="responsive"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '')
                .toString()
                .toLowerCase()
                .includes((input || '').trim().toLowerCase())
            }
          >
            {options.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          <Space>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa bộ lọc
            </Button>
          </Space>
        </div>
      );
    };

    return {
      filterDropdown: SelectFilter,
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: any) => {
        const recordValue = dataIndex.includes('.')
          ? dataIndex.split('.').reduce((obj, key) => obj?.[key], record)
          : record[dataIndex];
        if (Array.isArray(recordValue)) {
          return recordValue.includes(value);
        }
        return recordValue === value;
      },
    };
  };

  useEffect(() => {
    // Fetch banks
    BankService.getBankList().then((banks) => {
      setBankOptions(banks.map((b: any) => ({ value: b.id, label: b.name })));
    });
    // Fetch card types
    CardTypeService.getCardTypeList().then((types) => {
      setCardTypeOptions(
        types.map((t: any) => ({ value: t.cardTypeId || t.id, label: t.name }))
      );
    });
    // Fetch partners
    PartnerService.getAllPartners().then((partners) => {
      setPartnerOptions(
        partners.map((p: any) => ({ value: p.id, label: p.name }))
      );
    });
  }, []);

  const columns: ColumnsType<CreditCardResponse2> = [
    {
      title: 'Thẻ',
      key: 'creditCard',
      width: 140,
      fixed: 'left',
      render: (_, record) => (
        <div>
          <div className="font-mono font-medium text-blue-600">
            {formatCardNumber(record.cardNumber)}
          </div>
          <div className="font-medium text-gray-800">
            {record.cardHolderName || 'Chưa có tên'}
          </div>
          <div className="text-sm text-gray-500">{record.customer?.name}</div>
        </div>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
              <Input
                placeholder="Nhập 4 số cuối thẻ hoặc tên chủ thẻ"
                value={filters.search}
                onChange={(e) => {
                  onFiltersChange({ search: e.target.value });
                }}
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  onClick={() => {
                    clearFilters?.();
                    onFiltersChange({ search: '' });
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Xóa bộ lọc
                </Button>
              </Space>
            </div>
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.search ? [filters.search] : null,
        }
        : {
          filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }: any) => {
            return (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Nhập 4 số cuối thẻ hoặc tên chủ thẻ"
                  value={selectedKeys[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedKeys(value ? [value] : []);
                    setTimeout(() => confirm({ closeDropdown: false }), 100);
                  }}
                  onPressEnter={() => confirm()}
                  style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                  <Button
                    onClick={() => {
                      clearFilters();
                      confirm();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Space>
              </div>
            );
          },
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          onFilter: (value: any, record: any) => {
            const raw = Array.isArray(value) ? value[0] : value;
            if (raw == null || (typeof raw === 'string' && !raw.trim()))
              return true;
            const searchValue = (raw != null ? raw.toString() : '')
              .trim()
              .toLowerCase();

            const cardNumber = (record.cardNumber || '').toString();
            const last4Digits = cardNumber.slice(-4);
            const isCardNumberMatch = last4Digits.includes(searchValue);

            const cardHolderName = (record.cardHolderName || '')
              .toString()
              .toLowerCase();
            const isCardHolderNameMatch =
              cardHolderName.includes(searchValue);

            const customerName = (record.customer?.name || '')
              .toString()
              .toLowerCase();
            const isCustomerNameMatch = customerName.includes(searchValue);

            return (
              isCardNumberMatch ||
              isCardHolderNameMatch ||
              isCustomerNameMatch
            );
          },
        }),
    },
    {
      title: 'Ngân hàng',
      dataIndex: 'bank',
      key: 'bank',
      width: 100,
      render: (bank: any) => (
        <div>
          <div className="font-medium">{bank?.name}</div>
          <div className="text-sm text-gray-500">{bank?.bankCode}</div>
        </div>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          ...getSearchCheckboxFilterProps(
            'Ngân hàng',
            bankOptions,
            filters.bankIds,
            (vals) => onFiltersChange({ bankIds: vals })
          ),
          filteredValue: filters.bankIds.length ? filters.bankIds : null,
        }
        : {
          ...getSearchCheckboxFilterProps('Ngân hàng', bankOptions),
          onFilter: (value: any, record: any) => {
            return record.bank?.id === value;
          },
        }),
    },
    {
      title: 'Loại thẻ',
      dataIndex: 'cardType',
      key: 'cardType',
      width: 80,
      render: (cardType: any) => <Tag color="blue">{cardType?.name}</Tag>,
      ...(useServerFilters && filters && onFiltersChange
        ? {
          ...getSearchCheckboxFilterProps(
            'Loại thẻ',
            cardTypeOptions,
            filters.cardTypeIds,
            (vals) => onFiltersChange({ cardTypeIds: vals })
          ),
          filteredValue: filters.cardTypeIds.length
            ? filters.cardTypeIds
            : null,
        }
        : {
          ...getSearchCheckboxFilterProps('Loại thẻ', cardTypeOptions),
          onFilter: (value: any, record: any) => {
            return record.cardType?.id === value;
          },
        }),
    },

    {
      title: 'Trạng thái',
      key: 'status',
      width: 90,
      render: (_, record) => (
        <div className="space-y-1">
          {getStatusTag(record.status)}
          {getActiveStatusTag(record.isActive)}
        </div>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          ...getSimpleCheckboxFilterProps(
            'Trạng thái thẻ',
            [
              { value: 'TRA_THE', label: 'Trả thẻ' },
              { value: 'LUU_THE', label: 'Lưu thẻ' },
              { value: 'ACTIVE', label: 'Còn hạn' },
              { value: 'EXPIRED', label: 'Hết hạn' },
            ],
            [
              ...filters.statuses,
              ...(filters.isActive === true ? ['ACTIVE'] : []),
              ...(filters.isActive === false ? ['EXPIRED'] : []),
            ],
            (vals) => {
              const statuses = vals.filter(
                (v) => v === 'TRA_THE' || v === 'LUU_THE'
              );
              const isActiveVals = vals.filter(
                (v) => v === 'ACTIVE' || v === 'EXPIRED'
              );
              let isActive: boolean | null = null;
              if (isActiveVals.length === 1) {
                isActive = isActiveVals[0] === 'ACTIVE';
              }
              onFiltersChange({ statuses, isActive });
            }
          ),
          filteredValue: [
            ...filters.statuses,
            ...(filters.isActive === true ? ['ACTIVE'] : []),
            ...(filters.isActive === false ? ['EXPIRED'] : []),
          ],
        }
        : {
          ...getSimpleCheckboxFilterProps('Trạng thái thẻ', [
            { value: 'TRA_THE', label: 'Trả thẻ' },
            { value: 'LUU_THE', label: 'Lưu thẻ' },
            { value: 'ACTIVE', label: 'Còn hạn' },
            { value: 'EXPIRED', label: 'Hết hạn' },
          ]),
          onFilter: (value: any, record: any) => {
            if (value === 'ACTIVE') return record.isActive === true;
            if (value === 'EXPIRED') return record.isActive === false;
            return record.status === value;
          },
        }),
    },
    {
      title: 'Hoạt động',
      dataIndex: 'newStatus',
      key: 'newStatus',
      width: 100,
      render: (newStatus: string | undefined) => getNewStatusTag(newStatus),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          ...getSimpleCheckboxFilterProps(
            'Hoạt động',
            [
              { value: 'DANG_LAM', label: 'Đang làm' },
              { value: 'DANG_CHO', label: 'Đang chờ' },
              { value: 'KHONG_LAM', label: 'Không làm' },
            ],
            filters.newStatuses,
            (vals) => onFiltersChange({ newStatuses: vals })
          ),
          filteredValue: filters.newStatuses.length
            ? filters.newStatuses
            : null,
        }
        : {
          ...getSimpleCheckboxFilterProps('Hoạt động', [
            { value: 'DANG_LAM', label: 'Đang làm' },
            { value: 'DANG_CHO', label: 'Đang chờ' },
            { value: 'KHONG_LAM', label: 'Không làm' },
          ]),
          onFilter: (value: any, record: any) => {
            return record.newStatus === value;
          },
        }),
    },
    {
      title: 'CTV & Phí',
      key: 'partner',
      width: 150,
      render: (_, record) => {
        const partner = record.customer?.partner;
        const commissionRate = record.customer?.commissionRateForPartner;

        if (partner) {
          return (
            <div className="text-sm">
              <div className="font-medium text-purple-600">{partner.name}</div>
              {commissionRate && (
                <div className="text-xs text-gray-500">
                  Phí: {commissionRate}%
                </div>
              )}
            </div>
          );
        } else {
          return <span className="text-sm text-gray-400">Chưa có CTV</span>;
        }
      },
      ...(useServerFilters && filters && onFiltersChange
        ? {
          ...getSearchCheckboxFilterProps(
            'CTV & Phí',
            partnerOptions,
            filters.partnerIds,
            (vals) => onFiltersChange({ partnerIds: vals })
          ),
          filteredValue: filters.partnerIds.length
            ? filters.partnerIds
            : null,
        }
        : {
          ...getSearchCheckboxFilterProps('CTV & Phí', partnerOptions),
          onFilter: (value: any, record: any) => {
            return record.customer?.partner?.id === value;
          },
        }),
    },
    {
      title:
        selectedMonth && selectedYear
          ? `Lịch tháng ${selectedMonth}/${selectedYear}`
          : 'Lịch tháng này',
      key: 'monthlyScheduleStatus',
      width: 110,
      render: (_, record) => {
        const currentMonth = selectedMonth || dayjs().month() + 1; // dayjs month is 0-based
        const currentYear = selectedYear || dayjs().year();

        // Kiểm tra có lịch đáo hạn trong tháng hiện tại không
        const currentMonthSchedules =
          record.maturitySchedules?.filter((schedule) => {
            const scheduleDate = dayjs(schedule.dueDate);
            const scheduleMonth = scheduleDate.month() + 1;
            const scheduleYear = scheduleDate.year();

            return (
              scheduleMonth === currentMonth && scheduleYear === currentYear
            );
          }) || [];

        // Kiểm tra có lịch nào đã hoàn thành không (CHỈ COMPLETED)
        const hasCompletedSchedule = currentMonthSchedules.some((schedule) => {
          return schedule.status === 'COMPLETED';
        });

        if (hasCompletedSchedule) {
          return <Tag color="success">Hoàn thành</Tag>;
        } else if (currentMonthSchedules.length > 0) {
          return <Tag color="processing">Chưa hoàn thành</Tag>;
        } else {
          return <Tag color="default">Chưa có lịch</Tag>;
        }
      },
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => {
            const statusOptions: {
              value: ScheduleMonthBucket;
              label: string;
            }[] = [
                { value: 'completed', label: 'Hoàn thành' },
                { value: 'pending', label: 'Chưa hoàn thành' },
                { value: 'no_schedule', label: 'Chưa có lịch' },
              ];
            return (
              <div style={{ padding: 8 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Trạng thái lịch tháng này:</Text>
                </div>
                {statusOptions.map((option) => (
                  <div key={option.value} style={{ marginBottom: 4 }}>
                    <Checkbox
                      checked={filters.scheduleMonthBuckets.includes(
                        option.value
                      )}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const next = checked
                          ? [...filters.scheduleMonthBuckets, option.value]
                          : filters.scheduleMonthBuckets.filter(
                            (k) => k !== option.value
                          );
                        onFiltersChange({ scheduleMonthBuckets: next });
                      }}
                    >
                      {option.label}
                    </Checkbox>
                  </div>
                ))}
                <Space style={{ marginTop: 8 }}>
                  <Button
                    onClick={() => {
                      clearFilters?.();
                      onFiltersChange({ scheduleMonthBuckets: [] });
                      confirm();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Space>
              </div>
            );
          },
          filterIcon: (filtered: boolean) => (
            <FilterOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.scheduleMonthBuckets.length
            ? filters.scheduleMonthBuckets
            : null,
        }
        : {
          filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }: any) => {
            const statusOptions = [
              { value: 'completed', label: 'Hoàn thành' },
              { value: 'pending', label: 'Chưa hoàn thành' },
              { value: 'no_schedule', label: 'Chưa có lịch' },
            ];

            return (
              <div style={{ padding: 8 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Trạng thái lịch tháng này:</Text>
                </div>
                {statusOptions.map((option) => (
                  <div key={option.value} style={{ marginBottom: 4 }}>
                    <Checkbox
                      checked={selectedKeys.includes(option.value)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setSelectedKeys([...selectedKeys, option.value]);
                        } else {
                          setSelectedKeys(
                            selectedKeys.filter(
                              (key: string) => key !== option.value
                            )
                          );
                        }
                        setTimeout(() => confirm({ closeDropdown: false }), 100);
                      }}
                    >
                      {option.label}
                    </Checkbox>
                  </div>
                ))}
                <Space style={{ marginTop: 8 }}>
                  <Button
                    onClick={() => {
                      clearFilters();
                      confirm();
                    }}
                    size="small"
                    style={{ width: 90 }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Space>
              </div>
            );
          },
          filterIcon: (filtered: boolean) => (
            <FilterOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          onFilter: (value: any, record: any) => {
            const currentMonth = selectedMonth || dayjs().month() + 1;
            const currentYear = selectedYear || dayjs().year();

            const currentMonthSchedules =
              record.maturitySchedules?.filter((schedule: any) => {
                const scheduleDate = dayjs(schedule.dueDate);
                return (
                  scheduleDate.month() + 1 === currentMonth &&
                  scheduleDate.year() === currentYear
                );
              }) || [];

            const hasCompletedSchedule = currentMonthSchedules.some(
              (schedule: any) => schedule.status === 'COMPLETED'
            );

            if (value === 'completed') {
              return hasCompletedSchedule;
            } else if (value === 'pending') {
              return (
                currentMonthSchedules.length > 0 && !hasCompletedSchedule
              );
            } else if (value === 'no_schedule') {
              return currentMonthSchedules.length === 0;
            }
            return true;
          },
        }),
    },
    {
      title: 'Phí mặc định',
      dataIndex: 'defaultFeeRate',
      key: 'defaultFeeRate',
      width: 100,
      render: (defaultFeeRate: number | null | undefined) => (
        <span className="font-medium">
          {defaultFeeRate ? `${defaultFeeRate}%` : 'Chưa thiết lập'}
        </span>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
              <Input
                placeholder="Tìm phí mặc định"
                value={filters.defaultFeeRateContains}
                onChange={(e) =>
                  onFiltersChange({ defaultFeeRateContains: e.target.value })
                }
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  onClick={() => {
                    clearFilters?.();
                    onFiltersChange({ defaultFeeRateContains: '' });
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Xóa bộ lọc
                </Button>
              </Space>
            </div>
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.defaultFeeRateContains
            ? [filters.defaultFeeRateContains]
            : null,
        }
        : getColumnSearchProps('defaultFeeRate', 'phí mặc định')),
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'cyclePaymentDate',
      key: 'cyclePaymentDate',
      width: 120,
      render: (cyclePaymentDate: number | null) => (
        <span>
          {cyclePaymentDate
            ? `Ngày ${cyclePaymentDate} hàng tháng`
            : 'Chưa thiết lập'}
        </span>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
              <Select
                mode="multiple"
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Ngày thanh toán trong tháng"
                value={filters.cyclePaymentDays}
                onChange={(vals: number[]) => {
                  onFiltersChange({ cyclePaymentDays: vals });
                  confirm();
                }}
                allowClear
                options={Array.from({ length: 31 }, (_, i) => ({
                  value: i + 1,
                  label: `Ngày ${i + 1}`,
                }))}
              />
              <Space>
                <Button
                  onClick={() => {
                    clearFilters?.();
                    onFiltersChange({ cyclePaymentDays: [] });
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Xóa bộ lọc
                </Button>
              </Space>
            </div>
          ),
          filterIcon: (filtered: boolean) => (
            <FilterOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.cyclePaymentDays.length
            ? filters.cyclePaymentDays
            : null,
        }
        : {
          filters: Array.from({ length: 31 }, (_, i) => ({
            text: `Ngày ${i + 1}`,
            value: i + 1,
          })),
          ...getSelectFilterProps(
            'cyclePaymentDate',
            Array.from({ length: 31 }, (_, i) => ({
              value: i + 1,
              label: `Ngày ${i + 1}`,
            }))
          ),
        }),
    },
    {
      title: 'Hạn mức',
      dataIndex: 'creditLimit',
      key: 'creditLimit',
      width: 110,
      render: (creditLimit: number | null) => (
        <span className="font-medium text-green-600">
          {formatCurrency(creditLimit)}
        </span>
      ),
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
              <Input
                placeholder="Tìm hạn mức"
                value={filters.creditLimitContains}
                onChange={(e) =>
                  onFiltersChange({ creditLimitContains: e.target.value })
                }
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  onClick={() => {
                    clearFilters?.();
                    onFiltersChange({ creditLimitContains: '' });
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Xóa bộ lọc
                </Button>
              </Space>
            </div>
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.creditLimitContains
            ? [filters.creditLimitContains]
            : null,
        }
        : getColumnSearchProps('creditLimit', 'hạn mức')),
    },
    {
      title: 'Ngày hết hạn',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 100,
      render: (expiryDate: string) => {
        if (!expiryDate || !dayjs(expiryDate).isValid()) {
          return <span>-</span>;
        }

        return (
          <div>
            <div>{dayjs(expiryDate).format('DD/MM/YYYY')}</div>
            <div className="text-sm text-gray-500">
              {dayjs(expiryDate).isBefore(dayjs()) ? 'Đã hết hạn' : 'Còn hạn'}
            </div>
          </div>
        );
      },
      ...(useServerFilters && filters && onFiltersChange
        ? {
          filterDropdown: ({ confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
              <Input
                placeholder="Tìm ngày hết hạn"
                value={filters.expiryDateContains}
                onChange={(e) =>
                  onFiltersChange({ expiryDateContains: e.target.value })
                }
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: 'block' }}
              />
              <Space>
                <Button
                  onClick={() => {
                    clearFilters?.();
                    onFiltersChange({ expiryDateContains: '' });
                    confirm();
                  }}
                  size="small"
                  style={{ width: 90 }}
                >
                  Xóa bộ lọc
                </Button>
              </Space>
            </div>
          ),
          filterIcon: (filtered: boolean) => (
            <SearchOutlined
              style={{ color: filtered ? '#1890ff' : undefined }}
            />
          ),
          filteredValue: filters.expiryDateContains
            ? [filters.expiryDateContains]
            : null,
        }
        : getColumnSearchProps('expiryDate', 'ngày hết hạn')),
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết thẻ">
            <Button
              type="text"
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => handleViewCardDetail(record)}
            />
          </Tooltip>

          <Tooltip
            title={
              record.newStatus === 'KHONG_LAM'
                ? 'Kích hoạt thẻ'
                : 'Vô hiệu hóa thẻ'
            }
          >
            <Switch
              checked={record.newStatus !== 'KHONG_LAM'}
              onChange={() => handleToggleNewStatus(record)}
              size="small"
              style={{
                backgroundColor:
                  record.newStatus === 'KHONG_LAM' ? '#d1d5db' : '#10b981',
              }}
            />
          </Tooltip>

          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Chỉnh sửa',
                  icon: <EditOutlined style={{ color: '#1890ff' }} />,
                  onClick: () => handleEdit(record),
                },
                {
                  type: 'divider',
                },
                {
                  key: 'soft-delete',
                  label: 'Vô hiệu hóa',
                  icon: <DeleteOutlined style={{ color: '#fa8c16' }} />,
                  onClick: () => handleSoftDelete(record),
                  danger: true,
                },
                {
                  key: 'hard-delete',
                  label: (
                    <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                      Xóa vĩnh viễn
                    </span>
                  ),
                  icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                  onClick: () => handleHardDelete(record),
                  danger: true,
                },
              ],
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Tooltip title="Thêm tùy chọn">
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                className="text-gray-600 hover:text-gray-800"
              />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={
          serverPagination
            ? creditCards
            : [...creditCards].sort((a, b) => b.id - a.id)
        }
        rowKey="id"
        loading={loading}
        pagination={
          serverPagination
            ? {
              current: serverPagination.current,
              pageSize: serverPagination.pageSize,
              total: serverPagination.total,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (t, range) =>
                `${range[0]}-${range[1]} của ${t} thẻ tín dụng`,
            }
            : {
              pageSize: 20,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} thẻ tín dụng`,
            }
        }
        onChange={(pagination, filters, sorter, extra) => {
          // Chỉ xử lý chuyển trang khi hành động là paginate
          if (extra.action === 'paginate' && serverPagination && pagination.current) {
            serverPagination.onChange(pagination.current);
          }

          // Chỉ xử lý lọc khi hành động là filter
          if (extra.action === 'filter' && onFiltersChange && serverPagination) {
            const patch: any = {};
            if (filters.bank) patch.bankIds = filters.bank;
            if (filters.cardType) patch.cardTypeIds = filters.cardType;
            if (filters.status) {
              const statusVals = filters.status as string[];
              patch.statuses = statusVals.filter(
                (v) => v !== 'ACTIVE' && v !== 'EXPIRED'
              );
              const isActiveVals = statusVals.filter(
                (v) => v === 'ACTIVE' || v === 'EXPIRED'
              );
              if (isActiveVals.length === 1) {
                patch.isActive = isActiveVals[0] === 'ACTIVE';
              } else {
                patch.isActive = null;
              }
            }
            if (filters.newStatus) patch.newStatuses = filters.newStatus;
            if (filters.partner) patch.partnerIds = filters.partner;
            if (filters.monthlyScheduleStatus)
              patch.scheduleMonthBuckets = filters.monthlyScheduleStatus;
            if (filters.cyclePaymentDate)
              patch.cyclePaymentDays = filters.cyclePaymentDate;

            onFiltersChange(patch);
          }
        }}
        scroll={{ x: 1470 }}
        className="bg-white rounded-lg"
        size="middle"
        rowClassName="group hover:bg-gray-50"
      />

      {/* Edit Modal */}
      {selectedCard && (
        <EditCreditCardModal
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedCard(null);
          }}
          onSuccess={onUpdateSuccess}
          creditCard={selectedCard}
        />
      )}

      {/* View Modal */}
      {selectedCard && (
        <ViewCreditCardModal
          visible={viewModalVisible}
          onClose={() => {
            setViewModalVisible(false);
            setSelectedCard(null);
          }}
          creditCard={selectedCard}
        />
      )}
    </>
  );
};

export default CreditCardTable;
